import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link, useParams } from 'react-router';
import { ArrowLeft, Lock, Send } from 'lucide-react';

import { CopyButton } from '@/components/admin/copy-button';
import { StatusBadge } from '@/components/ui/status-badge';
import { Textarea } from '@/components/ui/textarea';
import { SUPPORT_REPLY_MAX_LENGTH } from '@/contracts';
import { useTranslations } from '@/i18n/use-locale';
import { formatCount } from '@/lib/format';
import { useAccountKindLabel, useSupportStatusLabel } from '@/lib/labels';
import { supportTicketTone } from '@/lib/status';
import { QueryGate } from '@/components/ui/query-gate';
import { useSupportTicketQuery } from '@/lib/queries';
import { useCloseTicketMutation, useReplyToTicketMutation } from '@/lib/mutations';
import { isoDate } from '@/lib/detail';
import { Card } from '@/components/ui/card';
import { MessageBubble } from './support-message-bubble';

/**
 * One support ticket: who wrote in, the conversation so far, and the two things the
 * team can do about it — reply, or close.
 *
 * The thread is the ticket itself rather than a separate list, so a reply or a close
 * puts the server's updated ticket straight into the cache; nothing is appended
 * optimistically and the status pill never disagrees with the messages under it.
 */
export function SupportTicketRoute() {
  const ticketId = useParams().ticketId ?? '';
  const t = useTranslations('admin.support');
  const statusLabel = useSupportStatusLabel();
  const kindLabel = useAccountKindLabel();
  const [reply, setReply] = useState('');
  const [closing, setClosing] = useState(false);

  const ticket = useSupportTicketQuery(ticketId);

  // Both writes land the same way — the answered ticket replaces the cached one and the
  // inbox is staled (`lib/mutations.ts`); what is left here is the words and the form.
  const replyMutation = useReplyToTicketMutation(ticketId, {
    okToast: t('toast.replied'),
    failToast: t('toast.failed'),
    onSuccess: () => setReply(''),
  });

  const closeMutation = useCloseTicketMutation(ticketId, {
    okToast: t('toast.closed'),
    failToast: t('toast.failed'),
    onSuccess: () => setClosing(false),
  });

  return (
    <QueryGate query={ticket}>
      {(data) => {
        const { requester } = data;
        const isClosed = data.status === 'closed';
        const trimmed = reply.trim();
        const canSend = trimmed.length > 0 && trimmed.length <= SUPPORT_REPLY_MAX_LENGTH;

        return (
          <div className="space-y-6">
            <Link
              to="/support"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-conc-500 transition hover:text-navy"
            >
              {/* Points back toward the inbox: left in English, right in Arabic. */}
              <ArrowLeft aria-hidden className="h-4 w-4 rtl:rotate-180" />
              {t('thread.back')}
            </Link>

            <Card asChild>
              <header className="p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-navy" dir="auto">
                      {data.subject}
                    </h2>
                    <p className="mt-0.5 text-[12px] text-conc-500">
                      {t('thread.opened', { date: isoDate(data.createdAt) ?? '' })}
                      {data.closedAt &&
                        ` · ${t('thread.closedOn', { date: isoDate(data.closedAt) ?? '' })}`}
                      {` · ${t('messageCount', { count: formatCount(data.messageCount) })}`}
                    </p>
                  </div>
                  <StatusBadge tone={supportTicketTone(data.status)}>
                    {statusLabel(data.status)}
                  </StatusBadge>
                </div>

                <dl className="mt-4 grid gap-3 rounded-xl bg-sideline p-3.5 text-[12.5px] sm:grid-cols-2">
                  <div>
                    <dt className="text-conc-500">{t('thread.requester')}</dt>
                    <dd className="font-semibold text-navy">
                      {requester.fullName || t('unnamedRequester')}
                      <span className="ms-1.5 font-normal text-conc-500">
                        · {kindLabel(requester.accountKind)}
                      </span>
                    </dd>
                  </div>
                  {requester.organizationName && (
                    <div>
                      <dt className="text-conc-500">{t('thread.organization')}</dt>
                      <dd className="font-semibold text-navy">{requester.organizationName}</dd>
                    </div>
                  )}
                  {requester.phone && (
                    <div>
                      <dt className="text-conc-500">{t('thread.phone')}</dt>
                      <dd className="flex items-center gap-1">
                        <span className="num ltr-field font-semibold text-navy">
                          {requester.phone}
                        </span>
                        <CopyButton
                          value={requester.phone}
                          label={t('thread.copyPhone')}
                          copiedLabel={t('thread.copied')}
                        />
                      </dd>
                    </div>
                  )}
                  {requester.email && (
                    <div>
                      <dt className="text-conc-500">{t('thread.email')}</dt>
                      <dd className="flex items-center gap-1">
                        <span className="ltr-field truncate font-semibold text-navy">
                          {requester.email}
                        </span>
                        <CopyButton
                          value={requester.email}
                          label={t('thread.copyEmail')}
                          copiedLabel={t('thread.copied')}
                        />
                      </dd>
                    </div>
                  )}
                  {!requester.phone && !requester.email && (
                    <div>
                      <dt className="sr-only">{t('thread.phone')}</dt>
                      <dd className="text-conc-400">{t('thread.noContact')}</dd>
                    </div>
                  )}
                </dl>
              </header>
            </Card>

            <ol aria-label={t('thread.conversation')} className="flex flex-col gap-3">
              {(data.messages ?? []).map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
            </ol>

            {isClosed ? (
              <p className="flex items-center gap-2 rounded-2xl border border-conc-100 bg-sideline p-4 text-[13px] text-conc-500">
                <Lock aria-hidden className="h-4 w-4 shrink-0" />
                {t('reply.closedNotice')}
              </p>
            ) : (
              <Card asChild>
                <form
                  className="space-y-3 p-5 shadow-sm"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (canSend && !replyMutation.isPending) replyMutation.mutate(trimmed);
                  }}
                >
                  <label className="block space-y-1.5">
                    <span className="text-[12.5px] font-semibold text-conc-500">
                      {t('reply.label')}
                    </span>
                    <Textarea
                      value={reply}
                      onChange={(event) => setReply(event.target.value)}
                      placeholder={t('reply.placeholder')}
                      maxLength={SUPPORT_REPLY_MAX_LENGTH}
                      dir="auto"
                      required
                    />
                  </label>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="num text-[11.5px] text-conc-400">
                      {t('reply.counter', {
                        count: formatCount(reply.length),
                        max: formatCount(SUPPORT_REPLY_MAX_LENGTH),
                      })}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="danger-soft"
                        size="sm"
                        onClick={() => setClosing((current) => !current)}
                      >
                        {t('close.action')}
                      </Button>
                      <Button
                        type="submit"
                        disabled={!canSend || replyMutation.isPending}
                        className="gap-1.5 py-2"
                      >
                        <Send aria-hidden className="h-4 w-4 rtl:-scale-x-100" />
                        {replyMutation.isPending ? t('reply.sending') : t('reply.send')}
                      </Button>
                    </div>
                  </div>

                  {closing && (
                    <div
                      role="group"
                      aria-label={t('close.title')}
                      className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/60 p-3"
                    >
                      <p className="text-[12.5px] text-navy">
                        {t('close.body', { subject: data.subject })}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="danger"
                          size="xs"
                          disabled={closeMutation.isPending}
                          onClick={() => closeMutation.mutate()}
                        >
                          {t('close.confirm')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          onClick={() => setClosing(false)}
                        >
                          {t('close.cancel')}
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
              </Card>
            )}
          </div>
        );
      }}
    </QueryGate>
  );
}
